import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
    recoveryCall,
    type RecoveryPage,
    type RecoveryRequest,
    type MailAttempt,
} from "@/api/recovery"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { auth } from "@/common/lib/firebase/firebase"

const describeDate = (value: number | null) =>
    value ? new Date(value).toLocaleString() : "Not confirmed"
function RetryMail({
    mail,
    retry,
    busy,
}: {
    mail: MailAttempt
    retry: (name: string, input: unknown) => void
    busy: boolean
}) {
    return (
        <li className="flex flex-wrap items-center gap-3 text-sm">
            <span>
                {mail.kind}:{" "}
                {mail.state === "accepted"
                    ? "Accepted by email provider"
                    : mail.state}{" "}
                (attempts: {mail.attempts || 0})
            </span>
            {(!["accepted", "obsolete", "pending"].includes(mail.state) ||
                (mail.state === "accepted" &&
                    ["contact", "replacement", "secondary"].includes(
                        mail.kind
                    ))) && (
                <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() =>
                        retry("retryRecoveryMail", { eventId: mail.id })
                    }
                >
                    {mail.state === "accepted"
                        ? "Resend verification"
                        : "Retry notification"}
                </Button>
            )}
        </li>
    )
}
function RequestReview({
    request,
    retry,
    busy,
}: {
    request: RecoveryRequest
    retry: (name: string, input: unknown) => void
    busy: boolean
}) {
    const [independentContact, setContact] = useState("")
    const [writtenAuthority, setAuthority] = useState("")
    const [reason, setReason] = useState("")
    const [approvalAttempted, setApprovalAttempted] = useState(false)
    const expired = request.expiresAt <= Date.now()
    const active =
        ["review", "awaiting_verification"].includes(request.status) && !expired
    const needsManual = !request.contactCurrent
    const contactMissing = approvalAttempted && !independentContact.trim()
    const authorityMissing = approvalAttempted && !writtenAuthority.trim()
    const self = request.uid === auth.currentUser?.uid
    return (
        <article className="border rounded-lg p-4 space-y-4">
            <div>
                <h3 className="font-semibold">
                    {request.firstName} {request.lastName}
                </h3>
                <p className="text-sm break-all">
                    {request.originalEmail} → {request.replacementEmail}
                </p>
                <p className="text-sm">
                    Status:{" "}
                    {expired &&
                    ["review", "awaiting_verification"].includes(request.status)
                        ? "Expired"
                        : request.status}{" "}
                    · Expires: {describeDate(request.expiresAt)}
                </p>
            </div>
            <dl className="text-sm space-y-2">
                <div>
                    <dt className="font-medium">Replacement inbox</dt>
                    <dd>{describeDate(request.replacementVerifiedAt)}</dd>
                </div>
                <div>
                    <dt className="font-medium">
                        Previously verified secondary
                    </dt>
                    <dd>
                        {request.contactEmail ||
                            "None enrolled at the time of request"}
                        {request.contactEmail && !request.contactCurrent
                            ? " (changed; manual verification required)"
                            : ""}
                    </dd>
                </div>
                <div>
                    <dt className="font-medium">Secondary approval</dt>
                    <dd>{describeDate(request.secondaryConfirmedAt)}</dd>
                </div>
            </dl>
            {request.manualEvidence && (
                <div className="text-sm whitespace-pre-wrap">
                    <p>
                        Independent contact:{" "}
                        {request.manualEvidence.independentContact}
                    </p>
                    <p>
                        Written authority:{" "}
                        {request.manualEvidence.writtenAuthority}
                    </p>
                </div>
            )}
            {request.reviewedBy && (
                <p className="text-sm">
                    Reviewed by: {request.reviewedBy} · Method:{" "}
                    {request.verificationMethod || "Rejected"}
                </p>
            )}
            {request.reason && (
                <p className="text-sm">Reason: {request.reason}</p>
            )}
            {self && <p role="alert">You cannot approve your own recovery.</p>}
            {active && !self && (
                <>
                    {needsManual ? (
                        <>
                            <p className="text-sm">
                                Contact the organisation using an independently
                                sourced channel. Obtain written authority naming
                                this representative. A charity number alone is
                                insufficient.
                            </p>
                            <div className="space-y-2">
                                <Label htmlFor={`${request.id}-contact`}>
                                    Independent contact evidence
                                </Label>
                                <Textarea
                                    id={`${request.id}-contact`}
                                    aria-invalid={contactMissing}
                                    aria-describedby={
                                        contactMissing
                                            ? `${request.id}-contact-error`
                                            : undefined
                                    }
                                    maxLength={2000}
                                    value={independentContact}
                                    onChange={(e) => setContact(e.target.value)}
                                    placeholder="Source of contact details, person contacted, date and outcome"
                                />
                                {contactMissing && (
                                    <p
                                        id={`${request.id}-contact-error`}
                                        role="alert"
                                        className="text-sm text-destructive"
                                    >
                                        Enter independent contact evidence.
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor={`${request.id}-authority`}>
                                    Written authority reference
                                </Label>
                                <Textarea
                                    id={`${request.id}-authority`}
                                    aria-invalid={authorityMissing}
                                    aria-describedby={
                                        authorityMissing
                                            ? `${request.id}-authority-error`
                                            : undefined
                                    }
                                    maxLength={2000}
                                    value={writtenAuthority}
                                    onChange={(e) =>
                                        setAuthority(e.target.value)
                                    }
                                    placeholder="Reference to retained written authority naming the replacement representative"
                                />
                                {authorityMissing && (
                                    <p
                                        id={`${request.id}-authority-error`}
                                        role="alert"
                                        className="text-sm text-destructive"
                                    >
                                        Enter a written authority reference.
                                    </p>
                                )}
                            </div>
                        </>
                    ) : (
                        <p className="text-sm">
                            The enrolled secondary contact must explicitly
                            confirm this request.
                        </p>
                    )}
                    <Button
                        disabled={
                            busy ||
                            !request.replacementVerifiedAt ||
                            (!needsManual && !request.secondaryConfirmedAt)
                        }
                        onClick={() => {
                            setApprovalAttempted(true)
                            if (
                                needsManual &&
                                (!independentContact.trim() ||
                                    !writtenAuthority.trim())
                            )
                                return
                            retry("reviewAccountRecovery", {
                                requestId: request.id,
                                action: "approve",
                                evidence: {
                                    independentContact,
                                    writtenAuthority,
                                },
                            })
                        }}
                    >
                        Approve and restore access
                    </Button>
                    <div className="space-y-2">
                        <Label htmlFor={`${request.id}-reason`}>
                            Reason for rejection
                        </Label>
                        <Textarea
                            id={`${request.id}-reason`}
                            value={reason}
                            maxLength={2000}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </div>
                    <Button
                        variant="destructive"
                        disabled={busy || !reason.trim()}
                        onClick={() =>
                            retry("reviewAccountRecovery", {
                                requestId: request.id,
                                action: "reject",
                                evidence: { reason },
                            })
                        }
                    >
                        Reject request
                    </Button>
                </>
            )}
            {["applying", "failed"].includes(request.status) && !self && (
                <Button
                    disabled={busy}
                    onClick={() =>
                        retry("retryAccountChange", { operationId: request.id })
                    }
                >
                    Resume account change
                </Button>
            )}
            <ul className="space-y-2">
                {request.notifications.map((mail) => (
                    <RetryMail
                        key={mail.id}
                        mail={mail}
                        retry={retry}
                        busy={busy}
                    />
                ))}
            </ul>
        </article>
    )
}

export function RecoveryReview() {
    const [cursors, setCursors] = useState<(string | null)[]>([null])
    const cursor = cursors[cursors.length - 1]
    const client = useQueryClient()
    const page = useQuery({
        queryKey: ["accountRecoveries", cursor],
        queryFn: () =>
            recoveryCall<RecoveryPage>("listAccountRecoveries", { cursor }),
    })
    const action = useMutation({
        mutationFn: ({ name, input }: { name: string; input: unknown }) =>
            recoveryCall(name, input),
        onSuccess: () => {
            client.invalidateQueries({ queryKey: ["accountRecoveries"] })
            client.invalidateQueries({ queryKey: ["pendingAccountTransfers"] })
        },
        onError: () => {
            client.invalidateQueries({ queryKey: ["accountRecoveries"] })
        },
    })
    const retry = (name: string, input: unknown) =>
        action.mutate({ name, input })
    return (
        <section className="bg-white rounded-lg border p-6 space-y-4">
            <div className="flex flex-wrap justify-between gap-3">
                <h2 className="text-xl font-semibold">
                    Account recovery review
                </h2>
                <Button
                    variant="outline"
                    onClick={() => page.refetch()}
                    disabled={page.isFetching}
                >
                    Refresh
                </Button>
            </div>
            <p className="text-sm">
                One administrator approves after all checks. Account changes and
                notification delivery are tracked separately. Retrying an
                unknown delivery may send a duplicate email; it does not repeat
                the account change.
            </p>
            {page.isPending && <p role="status">Loading requests…</p>}
            {page.isError && (
                <p role="alert">Unable to load recovery requests.</p>
            )}
            {action.isError && (
                <p role="alert" className="text-destructive">
                    {action.error.message}
                </p>
            )}
            {action.isSuccess && (
                <p role="status">
                    Operation recorded. Check the current status below.
                </p>
            )}
            {page.data && (
                <>
                    {page.data.requests.length === 0 && (
                        <p>No recovery requests on this page.</p>
                    )}
                    {page.data.requests.map((request) => (
                        <RequestReview
                            key={request.id}
                            request={request}
                            retry={retry}
                            busy={action.isPending}
                        />
                    ))}
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            disabled={cursors.length === 1}
                            onClick={() =>
                                setCursors((list) => list.slice(0, -1))
                            }
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            disabled={!page.data.nextCursor}
                            onClick={() =>
                                setCursors((list) => [
                                    ...list,
                                    page.data!.nextCursor,
                                ])
                            }
                        >
                            Next
                        </Button>
                    </div>
                    {page.data.operations.map((op) => (
                        <div key={op.id} className="text-sm space-y-2">
                            <p>
                                Ownership transfer {op.id}: {op.status},
                                checkpoint {op.failedStep ?? "in progress"}
                            </p>
                            <Button
                                disabled={
                                    action.isPending ||
                                    op.uid === auth.currentUser?.uid
                                }
                                onClick={() =>
                                    retry("retryAccountChange", {
                                        operationId: op.id,
                                    })
                                }
                            >
                                Resume ownership transfer
                            </Button>
                        </div>
                    ))}
                    {page.data.passwordNotifications.map((event) => (
                        <div key={event.id}>
                            <p className="text-sm">
                                Password operation: {event.uid} · {event.status}
                            </p>
                            {event.status === "notification_pending" ? (
                                <Button
                                    disabled={action.isPending}
                                    onClick={() =>
                                        retry("retryPasswordNotification", {
                                            eventId: event.id,
                                        })
                                    }
                                >
                                    Retry password notification
                                </Button>
                            ) : (
                                <p className="text-sm">
                                    If the attempt is no longer running, ask the
                                    user to request a fresh password reset link.
                                    Do not assume the password changed.
                                </p>
                            )}
                        </div>
                    ))}
                    {page.data.failedNotifications.length > 0 && (
                        <>
                            <h3 className="font-semibold">
                                Notifications needing attention
                            </h3>
                            <ul className="space-y-2">
                                {page.data.failedNotifications.map((mail) => (
                                    <RetryMail
                                        key={mail.id}
                                        mail={mail}
                                        retry={retry}
                                        busy={action.isPending}
                                    />
                                ))}
                            </ul>
                        </>
                    )}
                </>
            )}
        </section>
    )
}
